import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, MenuItem, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab,
  TextField, Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Tune as AdjustIcon, PointOfSale as CashierIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import LoyaltyService from '../../services/loyalty.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { apiError, formatDate, formatDateTime, formatMoney, formatPoints, ledgerColor } from './loyaltyUtils';

const Stat = ({ label, value, color }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" fontWeight="bold" color={color}>{value}</Typography>
  </Grid>
);

/**
 * Merchant-side customer profile. Transactions (commercial events) and
 * Points Activity (ledger) are deliberately separate tabs.
 */
const LoyaltyClientProfile = () => {
  const { uuid } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjust, setAdjust] = useState({ points_change: '', type: 'BONUS', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LoyaltyService.getClient(uuid);
      setData(res.data?.data || null);
    } catch (error) {
      setSnackbar({ open: true, message: apiError(error, t('loyalty.errorLoading')), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [uuid, t]);

  useEffect(() => { load(); }, [load]);

  const handleAdjust = async () => {
    const n = parseInt(adjust.points_change, 10);
    if (!n) return;
    try {
      setSaving(true);
      await LoyaltyService.adjustPoints(uuid, { ...adjust, points_change: n });
      setAdjustOpen(false);
      setAdjust({ points_change: '', type: 'BONUS', description: '' });
      setSnackbar({ open: true, message: t('loyalty.pointsAdjusted'), severity: 'success' });
      load();
    } catch (error) {
      setSnackbar({ open: true, message: apiError(error, t('loyalty.errorSaving')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  if (!data) return <Alert severity="error">{t('loyalty.customerNotFound')}</Alert>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3} flexWrap="wrap">
        <IconButton onClick={() => navigate('/loyalty/clients')}><ArrowBackIcon /></IconButton>
        <Box flexGrow={1}>
          <Typography variant="h4" fontWeight="bold">{data.full_name}</Typography>
          <Typography color="text.secondary">
            {data.phone}{data.email ? ` · ${data.email}` : ''}{data.birthday ? ` · ${t('loyalty.birthday')}: ${formatDate(data.birthday)}` : ''}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<CashierIcon />} onClick={() => navigate('/loyalty')}>{t('loyalty.cashierTitle')}</Button>
        {hasPermission('loyalty.manage') && (
          <Button variant="contained" startIcon={<AdjustIcon />} onClick={() => setAdjustOpen(true)}>{t('loyalty.adjustPoints')}</Button>
        )}
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Stat label={t('loyalty.currentPoints')} value={formatPoints(data.points_balance)} color="primary.main" />
            <Stat label={t('loyalty.lifetimePoints')} value={formatPoints(data.lifetime_points)} />
            <Stat label={t('loyalty.totalSpent')} value={formatMoney(data.total_spent)} />
            <Stat label={t('loyalty.visits')} value={data.visit_count} />
            <Stat label={t('loyalty.firstVisit')} value={formatDate(data.first_visit_at)} />
            <Stat label={t('loyalty.lastVisit')} value={formatDate(data.last_visit_at)} />
          </Grid>
          {data.tenant_client && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {t('nav.clients')}: {data.tenant_client.name}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
          <Tab label={t('loyalty.transactions')} />
          <Tab label={t('loyalty.pointsActivity')} />
          <Tab label={t('loyalty.rewardsRedeemed')} />
        </Tabs>
        <CardContent sx={{ overflowX: 'auto' }}>
          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.date')}</TableCell>
                  <TableCell>{t('loyalty.type')}</TableCell>
                  <TableCell>{t('loyalty.location')}</TableCell>
                  <TableCell align="right">{t('common.total')}</TableCell>
                  <TableCell align="right">{t('loyalty.points')}</TableCell>
                  <TableCell>{t('loyalty.cashier')}</TableCell>
                  <TableCell>{t('loyalty.reference')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recent_transactions.length === 0 && <TableRow><TableCell colSpan={7} align="center">—</TableCell></TableRow>}
                {data.recent_transactions.map((tx) => (
                  <TableRow key={tx.uuid}>
                    <TableCell>{formatDateTime(tx.occurred_at)}</TableCell>
                    <TableCell><Chip size="small" label={tx.type} color={tx.type === 'REFUND' ? 'error' : 'default'} /></TableCell>
                    <TableCell>{tx.location?.name}</TableCell>
                    <TableCell align="right">{formatMoney(tx.purchase_amount)}</TableCell>
                    <TableCell align="right">{tx.points_earned > 0 ? '+' : ''}{formatPoints(tx.points_earned)}</TableCell>
                    <TableCell>{tx.user?.name || '—'}</TableCell>
                    <TableCell>{tx.external_reference || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {tab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.date')}</TableCell>
                  <TableCell>{t('loyalty.type')}</TableCell>
                  <TableCell>{t('common.description')}</TableCell>
                  <TableCell align="right">{t('loyalty.change')}</TableCell>
                  <TableCell align="right">{t('loyalty.balanceBefore')}</TableCell>
                  <TableCell align="right">{t('loyalty.balanceAfter')}</TableCell>
                  <TableCell>{t('loyalty.by')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recent_ledger.length === 0 && <TableRow><TableCell colSpan={7} align="center">—</TableCell></TableRow>}
                {data.recent_ledger.map((e) => (
                  <TableRow key={e.uuid}>
                    <TableCell>{formatDateTime(e.created_at)}</TableCell>
                    <TableCell><Chip size="small" label={t(`loyalty.ledgerTypes.${e.type}`)} color={ledgerColor(e.type)} variant="outlined" /></TableCell>
                    <TableCell>{e.description || '—'}</TableCell>
                    <TableCell align="right"><b>{e.points_change > 0 ? '+' : ''}{formatPoints(e.points_change)}</b></TableCell>
                    <TableCell align="right">{formatPoints(e.balance_before)}</TableCell>
                    <TableCell align="right">{formatPoints(e.balance_after)}</TableCell>
                    <TableCell>{e.creator?.name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {tab === 2 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.date')}</TableCell>
                  <TableCell>{t('loyalty.reward')}</TableCell>
                  <TableCell>{t('loyalty.location')}</TableCell>
                  <TableCell align="right">{t('loyalty.pointsSpent')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recent_redemptions.length === 0 && <TableRow><TableCell colSpan={5} align="center">—</TableCell></TableRow>}
                {data.recent_redemptions.map((r) => (
                  <TableRow key={r.uuid}>
                    <TableCell>{formatDateTime(r.redeemed_at)}</TableCell>
                    <TableCell>{r.reward?.name}</TableCell>
                    <TableCell>{r.location?.name}</TableCell>
                    <TableCell align="right">-{formatPoints(r.points_spent)}</TableCell>
                    <TableCell><Chip size="small" label={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('loyalty.adjustPoints')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label={t('loyalty.type')} value={adjust.type} onChange={(e) => setAdjust({ ...adjust, type: e.target.value })}>
                <MenuItem value="BONUS">{t('loyalty.ledgerTypes.BONUS')}</MenuItem>
                <MenuItem value="MANUAL_ADJUSTMENT">{t('loyalty.ledgerTypes.MANUAL_ADJUSTMENT')}</MenuItem>
                <MenuItem value="POINTS_EXPIRED">{t('loyalty.ledgerTypes.POINTS_EXPIRED')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth type="number" label={t('loyalty.change')} helperText={t('loyalty.adjustHint')} value={adjust.points_change} onChange={(e) => setAdjust({ ...adjust, points_change: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label={t('common.description')} value={adjust.description} onChange={(e) => setAdjust({ ...adjust, description: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleAdjust} disabled={saving}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LoyaltyClientProfile;
